import './NarrativeDrivers.scss';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import CustomTooltip from '../../../../shared/components/common/tooltip/CustomTooltip';
import { SentimentDistribution } from '../../../../shared/models/context-insights/ContextInsightsResponse';
import StackedHorizontalBarChart from '../../../../shared/components/chart/stacked-horizontal-bar-chart/StackedHorizontalBarChart';
import SimpleBar from 'simplebar-react';
import useScrollFade from '../../../../shared/hooks/scroll/UseScrollFade';

interface NarrativeDriversProps {
  sentimentDistribution: SentimentDistribution[];
}

const NarrativeDrivers: React.FC<NarrativeDriversProps> = ({ sentimentDistribution }) => {
  const { t, i18n } = useTranslation();
  const indicatorLang: 'pl' | 'en' = i18n.language.startsWith('pl') ? 'pl' : 'en';

  const { scrollRef, isAtTop, isAtBottom, handleScroll } = useScrollFade();

  const sentimentBarData = useMemo(() => {
    if (!sentimentDistribution || sentimentDistribution.length === 0) {
      return [];
    }

    const colorMap = {
      POSITIVE: '#45A987',
      NEUTRAL: '#959596',
      NEGATIVE: '#CD3D44',
    };

    const order = ['POSITIVE', 'NEUTRAL', 'NEGATIVE'] as const;

    return order.map((sentiment) => {
      const item = sentimentDistribution.find((s) => s.sentiment === sentiment);
      return {
        label: t(sentiment.toLowerCase()),
        value: item ? Math.round(item.percentage) : 0,
        color: colorMap[sentiment],
      };
    });
  }, [sentimentDistribution, t]);

  const pickLocalized = (ind: { en: string; pl: string }) =>
    (ind[indicatorLang] || ind.en || '').trim();

  const positiveIndicators = useMemo(() => {
    const positive = sentimentDistribution.find((s) => s.sentiment === 'POSITIVE');
    return (positive?.topIndicators || []).map(pickLocalized).filter(Boolean);
  }, [sentimentDistribution, indicatorLang]);

  const neutralIndicators = useMemo(() => {
    const neutral = sentimentDistribution.find((s) => s.sentiment === 'NEUTRAL');
    return (neutral?.topIndicators || []).map(pickLocalized).filter(Boolean);
  }, [sentimentDistribution, indicatorLang]);

  const negativeIndicators = useMemo(() => {
    const negative = sentimentDistribution.find((s) => s.sentiment === 'NEGATIVE');
    return (negative?.topIndicators || []).map(pickLocalized).filter(Boolean);
  }, [sentimentDistribution, indicatorLang]);

  const topPositive = useMemo(() => {
    return sentimentDistribution.find((s) => s.sentiment === 'POSITIVE')?.count ?? 0;
  }, [sentimentDistribution]);

  const topNeutral = useMemo(() => {
    return sentimentDistribution.find((s) => s.sentiment === 'NEUTRAL')?.count ?? 0;
  }, [sentimentDistribution]);

  const topNegative = useMemo(() => {
    return sentimentDistribution.find((s) => s.sentiment === 'NEGATIVE')?.count ?? 0;
  }, [sentimentDistribution]);

  if (sentimentBarData.every((data) => data.value <= 0)) {
    return <></>;
  }

  return (
    <div className="stats__panel  narrative-drivers">
      <div className="page-box__title-action">
        <div className="page-box__title page-box__title--flex">
          {t('contextAnalysis.narrativeDrivers')}
          <CustomTooltip tooltipText="contextAnalysis.narrativeDriversTooltip" addMargin={false} />
        </div>
      </div>

      <div
        className={`stats-box ${isAtBottom ? 'stats-box--at-bottom' : ''} ${
          isAtTop ? 'stats-box--at-top' : ''
        }`}
      >
        <SimpleBar
          className="stats-box__scroll-box"
          style={{ maxWidth: '100%' }}
          autoHide={false}
          scrollableNodeProps={{ ref: scrollRef, onScroll: handleScroll }}
        >
          <div className="narrative-drivers__table">
            {topPositive > 0 && (
              <div className="narrative-drivers__row">
                <div className="narrative-drivers__row-header narrative-drivers__row-header--positive">
                  {t('positive')}
                </div>
                <div className="narrative-drivers__row-data">
                  {positiveIndicators.length > 0
                    ? positiveIndicators.join(', ')
                    : t('noExamplesAvailable')}
                </div>
              </div>
            )}
            {topNeutral > 0 && (
              <div className="narrative-drivers__row">
                <div className="narrative-drivers__row-header narrative-drivers__row-header--neutral">
                  {t('neutral')}
                </div>
                <div className="narrative-drivers__row-data">
                  {neutralIndicators.length > 0
                    ? neutralIndicators.join(', ')
                    : t('noExamplesAvailable')}
                </div>
              </div>
            )}
            {topNegative > 0 && (
              <div className="narrative-drivers__row">
                <div className="narrative-drivers__row-header narrative-drivers__row-header--negative">
                  {t('negative')}
                </div>
                <div className="narrative-drivers__row-data">
                  {negativeIndicators.length > 0
                    ? negativeIndicators.join(', ')
                    : t('noExamplesAvailable')}
                </div>
              </div>
            )}
            {sentimentBarData.length > 0 && <StackedHorizontalBarChart data={sentimentBarData} />}
          </div>
        </SimpleBar>
      </div>
    </div>
  );
};

export default NarrativeDrivers;
